<script>
    import { onMount } from 'svelte';
    import { isAuthenticated } from '$stores/sessionStore';
    import { navigate as goto } from 'svelte-routing';
    import Pagination from '$lib/Pagination.svelte';
    import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
    import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
    import { faChevronUp, faChevronDown, faRotate } from '@fortawesome/pro-light-svg-icons';
    
    // Use local API endpoints instead of direct API with key
    const API_BASE_URL = '/api/proxy';
  
    let stats = [];
    let limit = 200;
    let resultCount = 0;
    let currentPage = 1;
    let totalPages = 1;
    let isLoading = true;
  
    let currentDateTime = new Date().toLocaleString();
  
    onMount(async () => {
      isAuthenticated.subscribe((value) => {
        if (!$isAuthenticated) {
          goto('/login');
        }
      });
  
      //selectedStudy.set(null);
      selectedSite.set(null);
      selectedParticipant.set(null);
  
      if ($selectedStudy) {
        fetchStats(1);
      }
    });
  
    function handleParticipantClick(site, person) {
      selectedSite.set(site);
      selectedParticipant.set(person);
      goto(`/subjectZeros`);
    }
  
    function handleRefresh() {
      fetchStats(1);
    }
  
    function handlePageChange(newPage) {
      if (newPage > 0 && newPage <= totalPages) {
          fetchStats(newPage);
      }
    }
  
    async function fetchStats() {
      try {
        isLoading = true; // Set loading to true before starting the fetch
  
        const res = await fetch(
          `${API_BASE_URL}/stats/allSubjectsWithZeros?protocolNumber=${$selectedStudy.protocolNumber}`
        );
  
        if (!res.ok) {
          throw new Error('Failed to fetch stats');
        }
  
        const responseData = await res.json();
  
        resultCount = responseData.length;
  
        stats = responseData;
      } catch (err) {
        console.error('Error fetching stats:', err);
      } finally {
        isLoading = false; // Set loading to false after fetch is complete
      }
    }
  
    // Function to format Latest as local date string
    function formatLocalDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleString();
    }
  
    // Function to calculate time difference in days and hours
    function calculateTimeDifference(dateString) {
      const now = new Date();
      const pastDate = new Date(dateString);
      const diffMs = now - pastDate;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  
      // Return empty string if the difference is less than an hour
      if (diffDays === 0 && diffHours === 0) {
        return '';
      }
  
      // Return only the days part if the difference is a day or more
      if (diffDays >= 1) {
        return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
      }
  
      // Return the hours part if the difference is less than a day
      return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    }
  </script>
  
  <div class="flex justify-between ph-2 pb-0">
    <h2 class="text-s mb-4 text-sky-800 dark:text-white">
        {$selectedStudy ? "Study: " + $selectedStudy.studyName + " (" + $selectedStudy.protocolNumber + ")" : ""}
        {$selectedSite ? " - Site: " + $selectedSite.siteName + " (" + $selectedSite.siteId + ")" : ""}
        {$selectedParticipant ? " - Subject: " + $selectedParticipant.subjectId : ""}
    </h2> 
    <div class="text-m mb-0 text-sky-800 dark:text-white">
      {resultCount} Items
      <button on:click={() => handleRefresh()} class="pagination mx-1 p-0">
        <FontAwesomeIcon icon={faRotate} class="text-sky-800 dark:text-white" />
      </button>
    </div>
  </div>
  
  {#if isLoading}
    <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
      <div class="loader mb-4"></div>
      <p class="text-xs justify-center text-gray-700 dark:text-gray-300">
        preparing subject zeros report
      </p>
    </div>
  {:else}
    <div class="px-1 pt-0">
      <table class="min-w-full border-collapse table-auto">
        <thead>
          <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
            <th class="px-4 py-2 text-left bold">Site</th>
            <th class="px-4 py-2 text-left bold">Subject</th>
            <th class="px-4 py-2 text-left bold">Subject GUID</th>
            <th class="px-4 py-2 text-left bold">Device Id</th>
            <th class="px-4 py-2 text-left bold">Activity GUID</th>
            <th class="px-4 py-2 text-left bold">Count</th>
            <th class="px-4 py-2 text-left bold">First (Browser)</th>
            <th class="px-4 py-2 text-left bold">Last (Browser)</th>
          </tr>
        </thead>
        <tbody>
          {#each stats as stat, index}
            <tr
              class="text-sm hover:bg-stone-300 dark:hover:bg-gray-900/30 text-gray-800 dark:text-slate-300 {index %
                2 ===
              0
                ? 'bg-white dark:bg-gray-700'
                : 'bg-stone-100 dark:bg-gray-750'}"
            >
              <td class="px-4 py-2">{stat.Site.siteId}</td>
              <td class="px-4 py-2 cursor-pointer" on:click={() => handleParticipantClick(stat.Site, stat.Subject)}>{stat.Subject.subjectId}</td>
              <td class="px-4 py-2">{stat.subjectGUID}</td>
              <td class="px-4 py-2">{stat.deviceId}</td>
              <td class="px-4 py-2">{stat.subjectActivityGUID}</td>
              <td class="px-4 py-2">{stat.readingCount}</td>
              <td class="px-4 py-2">{formatLocalDate(stat.firstReading)}</td>
              <td class="px-4 py-2">{formatLocalDate(stat.lastReading)}</td>              
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
  
  <style>
    .loader {
      border: 2px solid #20669c;
      border-radius: 50%;
      border-top: 4px solid #98acb4;
      width: 32px;
      height: 32px;
      animation: spin 2s linear infinite;
    }
  
    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  </style>
  